// Libs
import classNames from 'classnames/bind';
import { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Box, Stack, Typography } from '@mui/material';
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
// Components, Layouts, Pages
import {
  BaseAvatarGroup,
  BaseFilters,
  BaseMoreAction,
  BaseSelect,
  BaseStatus,
  BaseTable,
  BaseToolbar,
  ConfirmModal,
  Loading,
} from '~/components/common';
import { TaskForm } from '~/components/module/task';
// Others
import { taskActions } from '~/thunks/task/taskSlice';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { TASK_PERMISSIONS } from '~/utils/constants/permissions';
import { IPaginationResponse } from '~/utils/interface/base';
import { IQueryParamTask, ITask } from '~/utils/interface/task';
import { formatDate, getFullName } from '~/utils/helper/common';
import { deleteTask, getListTask } from '~/thunks/task/taskThunk';
import { useAppDispatch, useAppSelector } from '~/utils/redux/hooks';
import { BusinessTypeEnum, DateFormatEnum } from '~/utils/enum/common';
import {
  IBaseMoreAction,
  IBaseOption,
  IFilterValueChange,
  IHeaderCellType,
  IPayloadCallApiFn,
  IResultCallApiFn,
} from '~/utils/interface/common';
import {
  DEFAULT_CURRENT_PAGE,
  DEFAULT_LIMIT_PAGE,
  DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA,
  EMPTY_STRING,
  EMPTY_VALUE,
  FILTER_DEFAULT_VALUES,
  optionTaskStatus,
} from '~/utils/constants/common';
import { getListUser } from '~/thunks/user/userThunk';
import { IQueryParamUser } from '~/utils/interface/user';
// Styles, images, icons
import styles from './Task.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const columnsTasks = (
  t: TFunction,
  onEdit: (record: ITask) => void,
  onDelete: (record: ITask) => void,
  isEdit: boolean,
  isDelete: boolean
): IHeaderCellType<ITask>[] => {
  return [
    {
      id: 'title',
      label: t('task_table_task_title'),
      render: (record: ITask) => (
        <Stack direction={'column'} maxWidth={350}>
          <Typography className={cx('textTitleTable')}>{`${record?.title} ${
            record?.businessType === BusinessTypeEnum.QUOTE || record?.businessType === BusinessTypeEnum.ORDER
              ? `#${record?.businessId}`
              : EMPTY_STRING
          }`}</Typography>
          <Typography className={cx('textNoteTable')}>{record?.description}</Typography>
        </Stack>
      ),
    },
    {
      id: 'assignees',
      label: t('task_table_assignee_title'),
      render: (record: ITask) => {
        const assignees = record?.assignees?.map((user) => {
          return {
            avatarImage: user.avatarImage,
            fullName: getFullName({
              firstName: user.firstName,
              lastName: user.lastName,
            }),
            id: user.id,
          };
        });

        return record?.assignees?.length ? <BaseAvatarGroup avatars={assignees} /> : EMPTY_VALUE;
      },
      minWidth: 140,
    },
    {
      id: 'due date',
      label: t('task_table_due_date_title'),
      render: (record: ITask) => {
        return (
          <Typography className={cx('textTable')}>
            {formatDate(record?.dueDate, DateFormatEnum.M_D_YYYY) || EMPTY_VALUE}
          </Typography>
        );
      },
      minWidth: 140,
    },
    {
      id: 'priority',
      label: t('task_table_priority_title'),
      render: (record: ITask) =>
        record?.priority ? <BaseStatus label={record?.priority || EMPTY_STRING} /> : EMPTY_VALUE,
    },
    {
      id: 'status',
      label: t('task_table_status_title'),
      render: (record: ITask) => (record?.status ? <BaseStatus label={record?.status || EMPTY_STRING} /> : EMPTY_VALUE),
    },
    {
      id: 'action',
      label: t('task_table_action_title'),
      minWidth: 100,
      align: 'center',
      render: (record: ITask) => {
        const actions: IBaseMoreAction[] = [
          ...(isEdit
            ? [
                {
                  label: t('table_action_edit', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionEdit}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  onClick: () => onEdit(record),
                },
              ]
            : []),
          ...(isDelete
            ? [
                {
                  label: t('table_action_delete', { ns: 'common/base' }),
                  icon: (
                    <img
                      src={icons.tableIconActionDelete}
                      alt={t('common_alt_img', { ns: 'common/base' })}
                      width={16}
                      height={16}
                    />
                  ),
                  hasOtherColor: true,
                  onClick: () => onDelete(record),
                },
              ]
            : []),
        ];

        return <BaseMoreAction actions={actions} />;
      },
    },
  ];
};

const Task = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canViewTasks, canEditTasks, canDeleteTasks } = usePermissions(TASK_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/jobs', 'common/base']);
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  const limitPage = useMemo<number>(() => Number(params?.limit ?? DEFAULT_LIMIT_PAGE), [params?.limit]);
  //#endregion Declare Hook

  //#region Selector
  const { isRefreshTaskList } = useAppSelector((state) => state.task);
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isShowFormTask, setIsShowFormTask] = useState<boolean>(false);
  const [isShowConfirmDelete, setIsShowConfirmDelete] = useState<boolean>(false);

  const [paramObject, setParamObject] = useState<IQueryParamTask>({
    page: pageSelected,
    limit: limitPage,
  });
  const [pagination, setPagination] = useState<IPaginationResponse>();
  const [filterParams, setFilterParams] = useState<IQueryParamTask>(FILTER_DEFAULT_VALUES);

  const [taskList, setTaskList] = useState<ITask[]>([]);
  const [taskIdSelected, setTaskIdSelected] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListTask(paramObject);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramObject]);

  useEffect(() => {
    if (!isRefreshTaskList) return;

    handleGetListTask(paramObject);

    dispatch(taskActions.setRefreshTaskList(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRefreshTaskList]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListTask = (params: IQueryParamTask) => {
    setIsLoading(true);

    dispatch(getListTask(params))
      .unwrap()
      .then((res) => {
        const { responses, pagination } = res.data;

        if (!responses) return;

        setTaskList(responses);
        setPagination(pagination);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamObject({ ...paramObject, page });
    setSearchParams({ ...params, page: String(page), limit: String(limitPage) });
  };

  const handleView = (item: ITask) => {
    if (!item) return;

    setTaskIdSelected(item?.id);
    handleShowFormTask();
  };

  const handleEdit = (item: ITask) => {
    if (!item) return;

    setTaskIdSelected(item?.id);
    handleShowFormTask();
  };

  const handleDelete = (item: ITask) => {
    if (!item) return;

    setTaskIdSelected(item?.id);
    setIsShowConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    setIsLoading(true);

    dispatch(deleteTask(taskIdSelected))
      .unwrap()
      .then(() => {
        handleCloseConfirmDelete();
        dispatch(taskActions.setRefreshTaskList(true));
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const fetchOptionFilter = useCallback(async (): Promise<IResultCallApiFn<IBaseOption>> => {
    const result: IResultCallApiFn<IBaseOption> = {
      optionsResp: optionTaskStatus,
      hasMoreResp: false,
    };

    return result;
  }, []);

  const handleApplyFilter = (values: IQueryParamTask | undefined) => {
    if (!values) {
      setFilterParams({});
      return;
    }

    setFilterParams({
      ...values,
    });

    if (values) {
      setParamObject({
        ...paramObject,
        status: values?.status,
        userId: values?.userId,
      });
    }
  };

  const handleClearFilter = () => {
    setFilterParams(FILTER_DEFAULT_VALUES);
    setParamObject({
      ...paramObject,
      page: DEFAULT_CURRENT_PAGE,
      limit: limitPage,
      status: undefined,
      userId: undefined,
    });
  };

  const handleFilterStatusChange = (
    status: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamTask>) => void
  ) => {
    if (!status) return;

    const { value } = status;

    onChange?.({ name: 'status', value: value });
    setFilterParams({ ...filterParams, status: value });
  };

  const handleFilterUserChange = (
    optionUser: IBaseOption,
    onChange?: (value: IFilterValueChange<IQueryParamTask>) => void
  ) => {
    if (!optionUser) return;

    const { value } = optionUser;

    onChange?.({ name: 'userId', value: value });
    setFilterParams({ ...filterParams, userId: value });
  };

  const fetchOptionUser = useCallback(async (payload: IPayloadCallApiFn): Promise<IResultCallApiFn<IBaseOption>> => {
    const { page, searchKey } = payload;

    const payloadParams: IQueryParamUser = {
      limit: DEFAULT_LIMIT_PAGE,
      page: page,
      ...(searchKey && { textSearch: searchKey }),
    };

    try {
      const resp = await dispatch(getListUser(payloadParams)).unwrap();

      const { responses, pagination } = resp?.data;

      const formatOptions: IBaseOption[] = responses.map((item) => ({
        value: item?.id,
        label: getFullName({ firstName: item?.firstName, lastName: item?.lastName }),
      }));

      const hasMoreResp: boolean = pagination.page < pagination.totalPages || false;

      return {
        optionsResp: formatOptions,
        hasMoreResp: hasMoreResp,
      };
    } catch (_error) {
      return DEFAULT_SELECT_DYNAMIC_RESULT_FETCH_DATA;
    }
  }, []);

  const handleCloseConfirmDelete = () => {
    setTaskIdSelected(EMPTY_STRING);
    setIsShowConfirmDelete(false);
  };

  const handleCloseFormTask = () => {
    setIsShowFormTask(false);
    setTaskIdSelected(EMPTY_STRING);
  };

  const handleShowFormTask = () => {
    setIsShowFormTask(true);
  };
  //#endregion Handle Function

  return (
    <Box id='taskListComponent' className={cx('container')}>
      <BaseToolbar title={t('task_title')}>
        <BaseFilters<IQueryParamTask>
          value={filterParams}
          defaultValue={FILTER_DEFAULT_VALUES}
          onApply={handleApplyFilter}
          onClear={handleClearFilter}
        >
          {({ value, onChange }) => (
            <Stack direction={'column'} width={340} mb={4} gap={6}>
              <BaseSelect
                gap={8}
                styleType='contained'
                label={t('job_title_filter_status')}
                placeholder={t('job_title_filter_all_status')}
                callApiFn={fetchOptionFilter}
                value={value?.status}
                onChange={(option) => handleFilterStatusChange(option as any, onChange)}
              />

              <BaseSelect
                isSearch
                gap={8}
                label={t('job_title_filter_user')}
                callApiFn={fetchOptionUser}
                type='loadMore'
                value={value?.userId}
                onChange={(option) => handleFilterUserChange(option as any, onChange)}
              />
            </Stack>
          )}
        </BaseFilters>
      </BaseToolbar>

      <Box component={'div'} className={cx('body')}>
        <BaseTable<ITask>
          dataSource={canViewTasks ? taskList : []}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columnsTasks(t, handleEdit, handleDelete, canEditTasks, canDeleteTasks)}
          onPageChange={handlePageChange}
          {...(canEditTasks ? { onRowClick: (_id, record) => handleView(record as ITask) } : {})}
        />
      </Box>

      {isShowFormTask && <TaskForm taskId={taskIdSelected} onClose={handleCloseFormTask} />}

      {isShowConfirmDelete && (
        <ConfirmModal
          type='delete'
          icon={icons.tableIconActionDelete}
          title={t('common_title_confirm_delete', { ns: 'common/base' })}
          textConfirm={t('common_action_delete', { ns: 'common/base' })}
          onClose={handleCloseConfirmDelete}
          onConfirm={handleConfirmDelete}
        />
      )}

      {isLoading && <Loading />}
    </Box>
  );
};

export default Task;
